import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * THANK YOU PAGE
 * ────────────────────────────────────────────────────────────────────────
 * Shown after a user successfully completes the "Reserve Bed" / OTP flow
 * in pgdetail.js. Two jobs:
 *
 * 1. UX: confirm the booking and tell the user what happens next.
 * 2. Tracking: give ad platforms (Meta/Instagram, Google Ads) and GA4 a
 *    single, reliable URL to treat as a "conversion" — but ONLY when the
 *    visitor actually arrived here via a real booking (see the `state`
 *    check below). Direct/organic visits never fire a conversion pixel.
 *
 * This page must NOT be indexed by search engines — see the noindex
 * effect below. Do not remove it.
 */

const ThankYou = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const bookingState = location.state || null;
  const pixelFired = useRef(false);

  // ── 1) Keep search engines out ────────────────────────────────────────
  // We inject <meta name="robots" content="noindex, nofollow"> only while
  // this page is mounted, and restore whatever was there before on
  // unmount, so it never leaks onto other routes in this single-page app.
  useEffect(() => {
    const existingTag = document.querySelector('meta[name="robots"]');
    const previousContent = existingTag ? existingTag.getAttribute("content") : null;
    let tag = existingTag;
    let createdNewTag = false;

    if (!tag) {
      tag = document.createElement("meta");
      tag.setAttribute("name", "robots");
      document.head.appendChild(tag);
      createdNewTag = true;
    }
    tag.setAttribute("content", "noindex, nofollow");

    const previousTitle = document.title;
    document.title = "Booking Confirmed" + (bookingState?.pgName ? ` — ${bookingState.pgName}` : "");

    return () => {
      if (createdNewTag) {
        tag.remove();
      } else if (previousContent !== null) {
        tag.setAttribute("content", previousContent);
      }
      document.title = previousTitle;
    };
  }, [bookingState]);

  // ── 2) Fire conversion tracking ONCE, only for real bookings ──────────
  // Gating on `bookingState.bookingId` (only ever set when we navigate()
  // here programmatically from pgdetail.js) means someone who finds this
  // URL directly — bookmark, shared link, stray crawler — can never
  // trigger a fake "Lead"/conversion event.
  useEffect(() => {
    if (!bookingState?.bookingId || pixelFired.current) return;
    pixelFired.current = true;

    try {
      // Meta / Instagram Ads pixel — requires the base Meta Pixel snippet
      // to already be loaded in index.html for window.fbq to exist.
      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        window.fbq("track", "Lead", {
          content_name: bookingState.pgName || "PG Booking",
          currency: "INR",
          value: 0,
        });
      }

      // Google Ads conversion — replace AW-XXXXXXXXX/XXXXXXXXXXXX with
      // the real conversion label from Google Ads > Conversions.
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "conversion", {
          send_to: "AW-XXXXXXXXX/XXXXXXXXXXXX",
        });

        // Also a clean GA4 event you can mark as a "key event"/conversion
        // in GA4 > Admin > Events, independent of Google Ads.
        window.gtag("event", "generate_lead", {
          currency: "INR",
          value: 0,
          pg_name: bookingState.pgName || undefined,
        });
      }
    } catch (err) {
      // Tracking must never break the page for the user.
      console.error("Thank-you page tracking error:", err);
    }
  }, [bookingState]);

  const hasRealBooking = Boolean(bookingState?.bookingId);

  return (
    <main className="thankyou-page">
      <div className="thankyou-card">
        {hasRealBooking ? (
          <>
            <div className="thankyou-icon">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                <circle cx="20" cy="20" r="20" fill="#DCFCE7" />
                <path
                  d="M11 20.5L16.5 26L29 13.5"
                  stroke="#16A34A"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            <h1 className="thankyou-title">Your booking request is in!</h1>
            <p className="thankyou-subtitle">
              Thanks{bookingState.tenantName ? `, ${bookingState.tenantName}` : ""} — we've received your
              request for{" "}
              <strong>{bookingState.pgName || "this PG"}</strong>.
            </p>

            <div className="thankyou-summary">
              {bookingState.visitLabel && (
                <div className="thankyou-summary-row">
                  <span className="thankyou-summary-label">Visit type</span>
                  <span className="thankyou-summary-value">{bookingState.visitLabel}</span>
                </div>
              )}
              {bookingState.formattedDate && (
                <div className="thankyou-summary-row">
                  <span className="thankyou-summary-label">Date</span>
                  <span className="thankyou-summary-value">{bookingState.formattedDate}</span>
                </div>
              )}
              {bookingState.selectedTime && (
                <div className="thankyou-summary-row">
                  <span className="thankyou-summary-label">Time</span>
                  <span className="thankyou-summary-value">{bookingState.selectedTime}</span>
                </div>
              )}
              {bookingState.email && (
                <div className="thankyou-summary-row">
                  <span className="thankyou-summary-label">Confirmation sent to</span>
                  <span className="thankyou-summary-value">{bookingState.email}</span>
                </div>
              )}
            </div>

            <div className="thankyou-next">
              <h2 className="thankyou-next-title">What happens next</h2>
              <ol className="thankyou-next-list">
                <li>The property manager will contact you directly to finalize your booking or visit.</li>
                <li>A confirmation summary has been sent via Email.</li>
              </ol>
            </div>

            <div className="thankyou-actions">
              <button className="thankyou-btn thankyou-btn-primary" onClick={() => navigate("/")}>
                Back to Home
              </button>
              <button className="thankyou-btn thankyou-btn-secondary" onClick={() => navigate("/listings")}>
                Browse more PGs
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="thankyou-icon thankyou-icon-neutral">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="#9CA3AF" strokeWidth="1.8" />
                <path d="M12 8v5" stroke="#9CA3AF" strokeWidth="1.8" strokeLinecap="round" />
                <circle cx="12" cy="16" r="1" fill="#9CA3AF" />
              </svg>
            </div>
            <h1 className="thankyou-title">We couldn't find a booking here</h1>
            <p className="thankyou-subtitle">
              This page is only shown right after you submit a Reserve Bed request. If you're looking to book a
              PG, head back to our listings to get started.
            </p>
            <div className="thankyou-actions">
              <button className="thankyou-btn thankyou-btn-primary" onClick={() => navigate("/listings")}>
                Browse PG Listings
              </button>
              <button className="thankyou-btn thankyou-btn-secondary" onClick={() => navigate("/")}>
                Go to Home
              </button>
            </div>
          </>
        )}
      </div>
    </main>
  );
};

export default ThankYou;